// The platform-neutral Waytide API client.
//
// `createClient(config)` wires the injected config (base URL + fetch + the
// session `TokenStore` + optional analytics hooks) into a single object that
// carries every method the web app and the future RN app need: the OTP +
// share auth handshakes, the session accessors, threads/messages/members,
// pause/resume, the live chat SSE subscriber, hours, operator-config, rules,
// trace, credit balance, phases/receipts, and shadow runs.
//
// Nothing here reads a build-time global, `window`, `localStorage`, or
// `EventSource`. The host supplies storage + config; web and RN differ only
// in those two injected pieces, not in this contract.

import type { ClientConfig } from "./config";
import { createRequest } from "./http";
import { createChatSubscriber } from "./sse";
import type { StreamHealth } from "./sse";
import type {
  Appearance,
  AuthResponse,
  BalanceResponse,
  ChatMessage,
  ClearRuleRequest,
  ClearRuleResponse,
  EvidenceResponse,
  OAuthProvider,
  DeviceCodeClaimResponse,
  GithubReposResponse,
  GithubConnectUrlResponse,
  BindRepoResponse,
  BoundRepoResponse,
  SchematicResponse,
  PullRequestDeskResponse,
  HoursResponse,
  OperatorConfigResponse,
  OperatorRule,
  PauseResponse,
  Phase,
  PhasesResponse,
  PhaseWithReceipt,
  RadioPolicy,
  RadioBudget,
  RadioClip,
  PreferencesResponse,
  InsightResponse,
  IngestedSessionsResponse,
  FocusState,
  SteeringPick,
  PresenceResponse,
  ProjectsResponse,
  AssignProjectResponse,
  ProjectGoalsResponse,
  ProjectWikiResponse,
  ProjectQuestionsResponse,
  BrainEvent,
  SessionPresence,
  ResolvedRuleset,
  SupervisorEventsResponse,
  ArbiterEventsResponse,
  SaveOperatorConfigRequest,
  SecondBrainResponse,
  SendDigestResponse,
  SessionSpendResponse,
  SetRuleRequest,
  ShadowRun,
  ShadowRunsResponse,
  ShareAuthResponse,
  Thread,
  ThreadMembersResponse,
  TopupCheckoutResponse,
  TopupResponse,
  TraceEvents,
  TraceStreamEvent,
} from "./types";
import type { SessionUser } from "./tokenStore";

/** The fully-wired client a host gets back from `createClient`. Every member
 * is bound to the injected config + store, so call sites need no globals. */
export interface WaytideClient {
  // ── Session (reads/writes the injected store) ──
  getToken(): string | null;
  getIdentity(): string | null;
  getUser(): SessionUser | null;
  saveSession(resp: AuthResponse): void;
  clearSession(): void;

  // ── Auth ──
  authShare(share_token: string): Promise<ShareAuthResponse>;
  authMe(): Promise<{
    identity: string;
    email: string;
    display_name: string;
    is_admin: boolean;
  }>;

  // ── OAuth sign-in (Google / Apple) ──
  /** The backend URL to navigate to, to start OAuth sign-in with `provider`.
   * The browser is sent here; the backend 302s to the provider, and its
   * callback hands the session back at `redirect` (the desktop passes its
   * `waytide://auth` deep link; omit it for the backend's default web
   * `/signin#token=…` return). A full-page navigation (NOT a fetch), so this
   * returns the URL for the host to open. */
  oauthStartUrl(provider: OAuthProvider, redirect?: string): string;
  /** Claim a one-time device code shown on the provider-callback page — the
   * fallback when the browser handoff never lands. Pre-session (`auth: false`),
   * so its 401 (bad/expired code) never clears an existing session. */
  claimDeviceCode(code: string): Promise<DeviceCodeClaimResponse>;

  // ── GitHub repo connection + repo-bind (per-project, NOT sign-in) ──
  /** The PROJECT's connected GitHub account's repositories, to drive the
   * MANDATORY repo pick when connecting a session. 409
   * (`github_not_connected`) until the project has a connection — prompt
   * `githubConnectUrl` then. */
  listProjectGithubRepos(project_id: number): Promise<GithubReposResponse>;
  /** The GitHub authorize URL to open in a (system) browser to connect the
   * project's GitHub account. Fetched authed (owner-gated mint); the signed
   * state in the URL carries the project. */
  githubConnectUrl(project_id: number): Promise<GithubConnectUrlResponse>;
  /** Bind a chosen GitHub repo to a session (the mandatory repo-bind, B2).
   * Membership-gated on the caller's JWT; a session is only fully connected once
   * it has a binding. */
  bindSessionRepo(
    thread_id: number,
    repo: {
      owner: string;
      name: string;
      full_name: string;
      default_branch: string;
    },
  ): Promise<BindRepoResponse>;
  /** Read a session's repo binding (membership-gated); `bound:false` when none. */
  getBoundRepo(thread_id: number): Promise<BoundRepoResponse>;
  /** The bound repo's PROJECT SCHEMATIC (Brief 5 / pt7) — the calm structural map
   * (areas + landmark files) the read/write transparency surface renders and an
   * op points a location at. Membership-gated; 409 when the session isn't bound
   * yet, 401 when the GitHub token needs reconnecting. Server-cached per
   * repo+branch, so re-opening a session is cheap. */
  getSchematic(thread_id: number): Promise<SchematicResponse>;

  // ── The PR desk ──
  /** Every DRAFT + OPEN pull request across the caller's projects, each with a
   * verdict on whether a human is needed (default: no). Newest-updated first. */
  listPullRequestDesk(): Promise<PullRequestDeskResponse>;

  // ── Operator events + digest ──
  listSupervisorEvents(): Promise<SupervisorEventsResponse>;
  listArbiterEvents(): Promise<ArbiterEventsResponse>;
  sendDigest(): Promise<SendDigestResponse>;

  // ── Threads + chat ──
  createThread(
    kind: Thread["kind"],
    title: string,
    member_emails: string[],
  ): Promise<Thread>;
  listThreads(): Promise<{ threads: Thread[] }>;
  /** `aside: true` posts a PRODUCT-SIDE-ONLY message (Brief 3 D6): it lives in
   * the thread for the humans + Operators (e.g. an Explain ask to Signal) and
   * the bridge never delivers it to the Agent.
   *
   * `idempotencyKey` (Brief 5 / point 15 — the comms artery) is a client-minted
   * key sent as the `Idempotency-Key` header so a REPLAYED send (the request
   * landed but its response was lost, then the outbox retried) can be de-duped
   * SERVER-side without double-posting. It is purely additive: omit it and the
   * call is byte-for-byte the historical one. A backend that does not yet honour
   * the header simply ignores it — the client-side outbox still prevents
   * reload/crash duplicates on its own (an entry clears only on confirmed
   * success); the header closes the remaining lost-response window once backend
   * dedup ships (see the follow-up flagged in `session/outbox.ts`). */
  sendThreadMessage(
    thread_id: number,
    body: string,
    aside?: boolean,
    idempotencyKey?: string,
  ): Promise<ChatMessage>;
  listThreadMessages(thread_id: number): Promise<{ messages: ChatMessage[] }>;

  // ── Members ──
  listThreadMembers(thread_id: number): Promise<ThreadMembersResponse>;
  addThreadMember(
    thread_id: number,
    member_email: string,
    member_kind?: string,
  ): Promise<ThreadMembersResponse>;

  // ── Pause / resume ──
  pauseSession(thread_id: number): Promise<PauseResponse>;
  resumeSession(thread_id: number): Promise<PauseResponse>;

  // ── Session presence (Brief 3 D1) ──
  getPresence(): Promise<PresenceResponse>;

  // ── Adherence insight (the corpus aggregate) ──
  /** The caller's ingested-session corpus reduced to the divergence/compaction
   * -vs-context thesis. Owner-scoped server-side. */
  getInsight(): Promise<InsightResponse>;
  /** List the caller's ingested sessions (metadata) — the corpus index, to
   * browse and open each session's full raw transcript. Owner-scoped. */
  getIngestedSessions(): Promise<IngestedSessionsResponse>;

  // ── Projects (Brief 4 / P8 — the Dashboard) ──
  /** The caller's projects, newest first. */
  listProjects(): Promise<ProjectsResponse>;
  /** Create a project owned by the caller (the owner is the JWT email, never a
   * body field) and return the refreshed list, so the sidebar updates in one
   * call. */
  createProject(name: string): Promise<ProjectsResponse>;
  /** Rename a project the caller owns; returns the refreshed list. Owner-gated
   * server-side on the verified JWT. */
  renameProject(project_id: number, name: string): Promise<ProjectsResponse>;
  /** Assign a session to a project (membership-gated on the caller). */
  assignSessionProject(
    thread_id: number,
    project_id: number,
  ): Promise<AssignProjectResponse>;
  /** A project's immovable goals/spec (the alignment anchor) as markdown. */
  getProjectGoals(project_id: number): Promise<ProjectGoalsResponse>;
  /** The project's wiki — the second brain Signal builds (Brief 5). */
  getProjectWiki(project_id: number): Promise<ProjectWikiResponse>;
  /** Signal's async questions for the project, with answers (Brief 5). */
  getProjectQuestions(project_id: number): Promise<ProjectQuestionsResponse>;
  /** Author/replace a project's immovable goals; returns the saved document. */
  putProjectGoals(
    project_id: number,
    markdown: string,
  ): Promise<ProjectGoalsResponse>;

  // ── User preferences (Brief 4 / P0b) ──
  getPreferences(): Promise<PreferencesResponse>;
  putPreferences(appearance: Appearance): Promise<PreferencesResponse>;

  // ── Live chat stream ──
  subscribeChat(
    onMessage: (m: ChatMessage) => void,
    onThread: (t: Thread) => void,
    onPresence?: (p: SessionPresence) => void,
    onTrace?: (t: TraceStreamEvent) => void,
    onPreferences?: (p: PreferencesResponse) => void,
    onBrain?: (b: BrainEvent) => void,
    onHealth?: (h: StreamHealth) => void,
  ): () => void;

  // ── Hours ──
  getHours(): Promise<HoursResponse>;
  topUpHours(hours: number): Promise<TopupResponse>;

  // ── Operator config ──
  getOperatorConfig(): Promise<OperatorConfigResponse>;
  saveOperatorConfig(
    cfg: SaveOperatorConfigRequest,
  ): Promise<OperatorConfigResponse>;

  // ── Rules ──
  getRules(project?: string, session?: string): Promise<ResolvedRuleset>;
  setRule(rule: SetRuleRequest): Promise<OperatorRule>;
  clearRule(rule: ClearRuleRequest): Promise<ClearRuleResponse>;

  // ── Presence heartbeat (Brief 5 pt16) + steering picks (pt17) ──
  /** Report this surface's user focus (active/backgrounded/idle) so the backend
   * knows whether the user is reachable in-app. Fire-and-forget. */
  postFocusHeartbeat(focus: FocusState): Promise<void>;
  /** Record the user's pick on a steering decision — an option id or typed
   * `other` free-text. Posts as the user's own steer + collapses the card. */
  pickSteeringDecision(decisionId: number, pick: SteeringPick): Promise<void>;

  // ── Trace + the second brain ──
  getTrace(project: string): Promise<TraceEvents>;
  getEvidence(project: string, ref: string): Promise<EvidenceResponse>;
  getSecondBrain(project: string): Promise<SecondBrainResponse>;

  // ── Radio (P4a) ──
  getRadioPolicy(): Promise<RadioPolicy>;
  getRadioBudget(thread: number): Promise<RadioBudget>;
  postRadioClip(thread: number, spoken: string): Promise<RadioClip>;

  // ── Credit balance ──
  getBalance(): Promise<BalanceResponse>;
  getSessionSpend(thread?: number): Promise<SessionSpendResponse>;
  topUpCredit(amount_usd: number): Promise<TopupCheckoutResponse>;

  // ── Phases + receipts ──
  listPhases(thread: number): Promise<PhasesResponse>;
  openPhase(
    thread_id: number,
    title: string,
    objective: string,
  ): Promise<Phase>;
  closePhase(
    phase_id: number,
    receipt_summary: string,
  ): Promise<PhaseWithReceipt>;
  getPhase(phase_id: number): Promise<PhaseWithReceipt>;

  // ── Shadow runs ──
  listShadowRuns(thread: number): Promise<ShadowRunsResponse>;
  openShadowRun(
    phase_id: number,
    objective: string,
    budget_micros: number,
  ): Promise<ShadowRun>;
}

/** Apply the arbiter default-true to a raw `GET /operator-config` body, so a
 * backend that omits any of the three arbiter flags is read as that arbiter
 * being ON (the client side of the shared wire contract: missing → true).
 * Pure + total, so it's unit-tested without a live backend and the page never
 * hand-codes the default. Exported so a host can reuse it on a body it
 * fetched separately. */
export function normalizeOperatorConfig(
  raw: OperatorConfigResponse,
): OperatorConfigResponse {
  return {
    ...raw,
    engineering_enabled: raw.engineering_enabled ?? true,
    design_enabled: raw.design_enabled ?? true,
    product_enabled: raw.product_enabled ?? true,
  };
}

/**
 * Build a fully-wired Waytide API client from an injected config.
 *
 * The host passes the backend origin, a `TokenStore`, and (optionally) a
 * fetch impl + analytics hooks; everything below is bound to those, so no
 * method touches a platform global.
 */
export function createClient(config: ClientConfig): WaytideClient {
  const request = createRequest(config);
  const subscribeChat = createChatSubscriber(config);
  const { store } = config;

  return {
    // ── Session ──
    getToken: () => store.getToken(),
    getIdentity: () => store.getIdentity(),
    getUser: () => store.getUser(),
    saveSession: (resp) => {
      store.setSession(resp);
      config.onSessionSaved?.(resp);
    },
    clearSession: () => {
      store.clear();
      config.onSessionCleared?.();
    },

    // ── Auth ──
    // M1 `POST /auth/share`: exchange a thread share token (carried in a deep
    // link) for a thread-scoped JWT. `auth: false` — this is a pre-session
    // handshake, so a 401 here must not clear an unrelated
    // existing session. The caller persists the returned scoped token however
    // its platform stores sessions (web: the same TokenStore; RN clip:
    // Keychain).
    authShare: (share_token) =>
      request("/auth/share", {
        method: "POST",
        auth: false,
        body: JSON.stringify({ share_token }),
      }),
    authMe: () => request("/auth/me"),

    // ── OAuth sign-in (Google / Apple) ──
    // A full-page navigation target, not a fetch: the browser goes to the
    // backend, which 302s to the provider. `apiBase` carries no trailing slash.
    oauthStartUrl: (provider, redirect) =>
      `${config.apiBase}/auth/oauth/${provider}/start${
        redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""
      }`,
    // Pre-session: a 401 (bad/expired code) must not clear an unrelated
    // existing session.
    claimDeviceCode: (code) =>
      request("/auth/device-code/claim", {
        method: "POST",
        auth: false,
        body: JSON.stringify({ code }),
      }),

    // ── GitHub repo connection + repo-bind (per-project, NOT sign-in) ──
    listProjectGithubRepos: (project_id) =>
      request(`/projects/${project_id}/github/repos`),
    githubConnectUrl: (project_id) =>
      request(`/projects/${project_id}/github/connect/url`),
    bindSessionRepo: (thread_id, repo) =>
      request(`/threads/${thread_id}/repo`, {
        method: "POST",
        body: JSON.stringify(repo),
      }),
    getBoundRepo: (thread_id) => request(`/threads/${thread_id}/repo`),
    getSchematic: (thread_id) => request(`/threads/${thread_id}/schematic`),
    listPullRequestDesk: () => request("/pull-requests"),

    // ── Operator events + digest ──
    listSupervisorEvents: () => request("/supervisor-events"),
    listArbiterEvents: () => request("/arbiter-events"),
    sendDigest: () => request("/digest", { method: "POST" }),

    // ── Threads + chat ──
    createThread: (kind, title, member_emails) =>
      request("/threads", {
        method: "POST",
        body: JSON.stringify({ kind, title, member_emails }),
      }),
    listThreads: () => request("/threads"),
    sendThreadMessage: (thread_id, body, aside, idempotencyKey) =>
      request(`/threads/${thread_id}/messages`, {
        method: "POST",
        body: JSON.stringify(aside ? { body, aside: true } : { body }),
        // Additive header; absent when no key is supplied, so the request is
        // unchanged for callers that don't opt into idempotent replay.
        ...(idempotencyKey
          ? { headers: { "Idempotency-Key": idempotencyKey } }
          : {}),
      }),
    listThreadMessages: (thread_id) =>
      request(`/threads/${thread_id}/messages`),

    // ── Members ──
    listThreadMembers: (thread_id) =>
      request(`/threads/${thread_id}/members`),
    addThreadMember: (thread_id, member_email, member_kind) => {
      const body: { member_email: string; member_kind?: string } = {
        member_email,
      };
      if (member_kind !== undefined) body.member_kind = member_kind;
      return request(`/threads/${thread_id}/members`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },

    // ── Pause / resume ──
    pauseSession: (thread_id) =>
      request(`/threads/${thread_id}/pause`, { method: "POST" }),
    resumeSession: (thread_id) =>
      request(`/threads/${thread_id}/resume`, { method: "POST" }),

    // ── Session presence ──
    getPresence: () => request("/presence"),

    // ── Adherence insight + corpus ──
    getInsight: () => request("/ingest/insight"),
    getIngestedSessions: () => request("/ingest/sessions"),

    // ── Projects (Brief 4 / P8) ──
    listProjects: () => request("/projects"),
    createProject: (name) =>
      request("/projects", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    renameProject: (project_id, name) =>
      request(`/projects/${project_id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    assignSessionProject: (thread_id, project_id) =>
      request(`/threads/${thread_id}/project`, {
        method: "POST",
        body: JSON.stringify({ project_id }),
      }),
    getProjectGoals: (project_id) => request(`/projects/${project_id}/goals`),
    getProjectWiki: (project_id) => request(`/projects/${project_id}/wiki`),
    getProjectQuestions: (project_id) =>
      request(`/projects/${project_id}/questions`),
    putProjectGoals: (project_id, markdown) =>
      request(`/projects/${project_id}/goals`, {
        method: "PUT",
        body: JSON.stringify({ markdown }),
      }),

    // ── User preferences ──
    getPreferences: () => request("/preferences"),
    putPreferences: (appearance) =>
      request("/preferences", {
        method: "PUT",
        body: JSON.stringify({ appearance }),
      }),

    // ── Live chat stream ──
    subscribeChat,

    // ── Hours ──
    getHours: () => request("/hours"),
    topUpHours: (hours) =>
      request("/hours/topup", {
        method: "POST",
        body: JSON.stringify({ hours }),
      }),

    // ── Operator config ──
    getOperatorConfig: async () =>
      normalizeOperatorConfig(await request("/operator-config")),
    saveOperatorConfig: async (cfg) =>
      normalizeOperatorConfig(
        await request("/operator-config", {
          method: "PUT",
          body: JSON.stringify(cfg),
        }),
      ),

    // ── Rules ──
    getRules: (project, session) => {
      const params = new URLSearchParams();
      if (project) params.set("project", project);
      if (session) params.set("session", session);
      const query = params.toString();
      return request(`/rules${query ? `?${query}` : ""}`);
    },
    setRule: (rule) =>
      request("/rules", {
        method: "POST",
        body: JSON.stringify(rule),
      }),
    clearRule: (rule) =>
      request("/rules", {
        method: "DELETE",
        body: JSON.stringify(rule),
      }),

    // ── Presence heartbeat (pt16) + steering picks (pt17) ──
    postFocusHeartbeat: (focus) =>
      request("/focus/heartbeat", {
        method: "POST",
        body: JSON.stringify({ focus }),
      }),
    pickSteeringDecision: (decisionId, pick) =>
      request(`/steering/${decisionId}/pick`, {
        method: "POST",
        body: JSON.stringify(pick),
      }),

    // ── Trace + the second brain ──
    getTrace: (project) => {
      const params = new URLSearchParams({ project });
      return request(`/trace?${params.toString()}`);
    },
    getEvidence: (project, ref) => {
      const params = new URLSearchParams({ project, ref });
      return request(`/trace/evidence?${params.toString()}`);
    },
    getSecondBrain: (project) => {
      const params = new URLSearchParams({ project });
      return request(`/second-brain?${params.toString()}`);
    },

    // ── Radio (P4a) ──
    getRadioPolicy: () => request("/radio/policy"),
    getRadioBudget: (thread) => request(`/radio/budget?thread=${thread}`),
    postRadioClip: (thread, spoken) =>
      request("/radio/clip", {
        method: "POST",
        body: JSON.stringify({ thread_id: thread, spoken }),
      }),

    // ── Credit balance ──
    getBalance: () => request("/balance"),
    getSessionSpend: (thread) => {
      const query =
        thread === undefined
          ? ""
          : `?${new URLSearchParams({ thread: String(thread) })}`;
      return request(`/balance/sessions${query}`);
    },
    topUpCredit: (amount_usd) =>
      request("/billing/topup", {
        method: "POST",
        body: JSON.stringify({ amount_usd }),
      }),

    // ── Phases + receipts ──
    listPhases: (thread) => {
      const params = new URLSearchParams({ thread: String(thread) });
      return request(`/phases?${params.toString()}`);
    },
    openPhase: (thread_id, title, objective) =>
      request("/phases", {
        method: "POST",
        body: JSON.stringify({ thread_id, title, objective }),
      }),
    closePhase: (phase_id, receipt_summary) =>
      request(`/phases/${phase_id}/close`, {
        method: "POST",
        body: JSON.stringify({ receipt_summary }),
      }),
    getPhase: (phase_id) => request(`/phases/${phase_id}`),

    // ── Shadow runs ──
    listShadowRuns: (thread) => {
      const params = new URLSearchParams({ thread: String(thread) });
      return request(`/shadow-runs?${params.toString()}`);
    },
    openShadowRun: (phase_id, objective, budget_micros) =>
      request("/shadow-runs", {
        method: "POST",
        body: JSON.stringify({ phase_id, objective, budget_micros }),
      }),
  };
}
